
import React, { useState, useEffect, useRef } from 'react';
import { 
    Plus, Trash2, Save, Stethoscope, 
    Zap, Sparkles, Loader2, Wand2, Brain, 
    ChevronRight, CheckCircle, Info, Database,
    Layout, Target, Image as ImageIcon, Search,
    RefreshCw, ShieldCheck, ListChecks, Settings,
    AlertCircle, Server, Download, Upload, FileJson,
    AlertTriangle, X, Check,
    ChevronUp,
    ChevronDown
} from 'lucide-react';
import { ScenarioChallenge, CertificationType, BackendQuestion } from '../../types';
import { StorageService } from '../../services/storageService';
import { PageLoader } from '../../components/PageLoader';
import { generateScenarioItems, generateScenarioStrategy } from '../../services/geminiService';

export const ScenarioManager: React.FC = () => {
    const [certs, setCerts] = useState<CertificationType[]>([]);
    const [certId, setCertId] = useState('');
    const [scenarios, setScenarios] = useState<ScenarioChallenge[]>([]);
    const [selectedScenario, setSelectedScenario] = useState<Partial<ScenarioChallenge> | null>(null);
    const [activeCollection, setActiveCollection] = useState<string>('pending...');
    
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [status, setStatus] = useState<{type: 'error' | 'success' | 'warning', text: string} | null>(null);
    
    // AI Strategy / Suggestions
    const [isBlueprinting, setIsBlueprinting] = useState(false);
    const [suggestedTopics, setSuggestedTopics] = useState<string[]>([]);
    
    // AI Builder States
    const [showBuilder, setShowBuilder] = useState(false);
    const [builderTopic, setBuilderTopic] = useState('');
    const [builderCount, setBuilderCount] = useState(10);

    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const init = async () => {
            setIsLoading(true);
            try {
                const c = await StorageService.getCertifications();
                setCerts(c);
                if (c.length > 0) setCertId(c[0].id.toLowerCase());
            } catch (e) {} finally {
                setIsLoading(false);
            }
        };
        init();
    }, []);

    const fetchScenarios = async (id: string) => {
        if (!id) return;
        setIsLoading(true);
        try {
            const data = await StorageService.getScenarioChallenges(id);
            setScenarios(data.items || []);
            if (data.collection) setActiveCollection(data.collection);
        } catch (e) {
            console.error("Fetch failed", e);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!certId) return;
        fetchScenarios(certId);
    }, [certId]);

    const activeCert = certs.find(c => c.id === certId);

    const handleSuggestStrategy = async () => {
        setIsBlueprinting(true);
        setStatus(null);
        try {
            const topics = await generateScenarioStrategy(activeCert?.name || 'Medical');
            setSuggestedTopics(topics);
            setStatus({ type: 'success', text: 'AI Strategy Synthesized. Expand topics to Provision.' });
        } catch (e) {
            setStatus({ type: 'error', text: 'Strategy engine interrupted.' });
        } finally {
            setIsBlueprinting(false);
        }
    };

    const handleProvisionFromTopic = async (topic: string, autoBuild: boolean = false) => {
        if (autoBuild) {
            setBuilderTopic(topic);
            setShowBuilder(true);
            setStatus(null);
        } else {
            const newScenario: Partial<ScenarioChallenge> = {
                id: `manual-${Date.now()}`,
                certId: certId.toLowerCase(),
                title: topic,
                description: `Clinical logic sequence for ${topic}.`,
                questionCount: 10,
                difficulty: 'Medium',
                isActive: true,
                questions: []
            };
            setSelectedScenario(newScenario);
            setStatus({ type: 'success', text: `Empty sequence provisioned: ${topic}` });
        }
        setSuggestedTopics(prev => prev.filter(t => t !== topic));
    };

    const performScenarioSynthesis = async (topic: string, count: number) => {
        if (!topic.trim() || !certId) return;
        setIsGenerating(true);
        setStatus(null);
        try {
            const certName = activeCert?.name || 'Medical';
            const questions = await generateScenarioItems(topic, count, certName);
            
            const sanitizedQuestions = questions.map((q, qIdx) => ({
                ...q,
                _id: q._id || `gen_scen_${Date.now()}_${qIdx}`,
                difficulty: q.difficulty || 'Medium',
                type: q.type || 'multiple-choice',
                options: q.options.map((o, oIdx) => ({
                    ...o,
                    _id: o._id || `gen_opt_${Date.now()}_${qIdx}_${oIdx}`
                }))
            }));

            const newScenario: Partial<ScenarioChallenge> = {
                id: `challenge-${Date.now()}`,
                certId: certId.toLowerCase(),
                title: topic,
                description: `A sequential clinical case study focusing on ${topic}.`,
                questionCount: count,
                difficulty: 'Medium',
                isActive: true,
                questions: sanitizedQuestions as BackendQuestion[]
            };
            
            setSelectedScenario(newScenario);
            setStatus({ type: 'success', text: 'AI logic sequence synthesized. Review and commit to save.' });
            return newScenario;
        } catch (e) {
            setStatus({ type: 'error', text: 'AI synthesis interrupted.' });
        } finally {
            setIsGenerating(false);
        }
    };

    const handleBuildWithAI = async () => {
        const result = await performScenarioSynthesis(builderTopic, builderCount);
        if (result) {
            setShowBuilder(false);
            setBuilderTopic('');
        }
    };

    const handleSave = async () => {
        if (!selectedScenario?.title || !selectedScenario?.id || !certId) return;
        setIsSaving(true);
        setStatus(null);
        try {
            const currentCertId = certId.toLowerCase();
            const sanitizedQuestions = selectedScenario.questions?.map((q, qIdx) => ({
                ...q,
                _id: q._id || `gen_scen_${Date.now()}_${qIdx}`,
                options: q.options.map((o, oIdx) => ({
                    ...o,
                    _id: o._id || `opt_${Date.now()}_${qIdx}_${oIdx}`
                }))
            }));

            const payload = { 
                ...selectedScenario, 
                certId: currentCertId,
                questions: sanitizedQuestions 
            };

            const response = await StorageService.saveScenarioChallenge(currentCertId, payload);
            setStatus({ type: 'success', text: `Committed to Registry: ${response.collection || activeCollection}` });
            await fetchScenarios(currentCertId);
            setSelectedScenario(null);
        } catch (e: any) {
            console.error("Scenario Save Failure:", e);
            setStatus({ type: 'error', text: `Registry Sync Aborted: ${e.message || 'Check server logs for schema violations.'}` });
        } finally {
            setIsSaving(false);
        }
    };

    const handleExport = () => {
        if (scenarios.length === 0) return;
        setIsExporting(true);
        try {
            const dataStr = JSON.stringify(scenarios, null, 2);
            const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
            const fileName = `${certId}_scenarios_registry_${new Date().toISOString().split('T')[0]}.json`;
            const linkElement = document.createElement('a');
            linkElement.setAttribute('href', dataUri);
            linkElement.setAttribute('download', fileName);
            linkElement.click();
            setStatus({ type: 'success', text: 'Scenario registry export package generated.' });
        } catch (e) {
            setStatus({ type: 'error', text: 'Export failed.' });
        } finally {
            setIsExporting(false);
        }
    };

    const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        setIsImporting(true);
        setStatus({ type: 'warning', text: 'Validating scenario schema...' });
        
        const reader = new FileReader();
        reader.onload = async (event) => {
            try {
                const text = event.target?.result as string;
                const data = JSON.parse(text);
                
                if (!Array.isArray(data)) throw new Error("Invalid JSON: Root must be an array of Scenario Challenges.");
                
                const isValid = data.every(s => s.id && s.title && Array.isArray(s.questions));
                if (!isValid) throw new Error("Schema Violation: Some challenges lack required fields.");

                const ingested = data.map(s => ({ ...s, certId: certId.toLowerCase() }));

                await StorageService.saveScenariosBulk(certId.toLowerCase(), ingested);
                setStatus({ type: 'success', text: `Successfully ingested ${data.length} scenarios into ${certId.toUpperCase()} registry.` });
                fetchScenarios(certId.toLowerCase());
            } catch (err: any) {
                setStatus({ type: 'error', text: `Ingestion Failed: ${err.message}` });
            } finally {
                setIsImporting(false);
                if (fileInputRef.current) fileInputRef.current.value = '';
            }
        };
        reader.readAsText(file);
    };

    const deleteScenario = async (id: string) => {
        if (!confirm('Permanently purge this challenge?')) return;
        try {
            await StorageService.deleteScenarioChallenge(certId, id);
            setScenarios(prev => prev.filter(s => s.id !== id));
            setStatus({ type: 'success', text: 'Challenge purged.' });
        } catch (e) {}
    };

    return (
        <div className="space-y-8 animate-fade-in pb-24 h-full flex flex-col text-slate-900">
            {showBuilder && (
                <div className="fixed inset-0 z-[250] bg-slate-950/80 backdrop-blur-md flex items-start justify-center p-4 pt-10 md:pt-16 overflow-y-auto custom-scrollbar">
                    <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-lg h-auto overflow-hidden animate-pop">
                        <div className="p-8 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                            <h3 className="text-xl font-black text-slate-900 flex items-center gap-3"><Sparkles size={24} className="text-indigo-500" /> AI Scenario Builder</h3>
                            <button onClick={() => setShowBuilder(false)}><X size={28} /></button>
                        </div>
                        <div className="p-10 space-y-8">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Case Study Focus</label>
                                <input 
                                    type="text" 
                                    value={builderTopic} 
                                    onChange={e => setBuilderTopic(e.target.value)}
                                    placeholder="e.g., Alzheimer's Resident Care"
                                    className="w-full p-6 bg-slate-50 border border-slate-100 rounded-[2rem] outline-none font-black text-xl shadow-inner focus:bg-white transition-all"
                                    autoFocus
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Logic Depth (Items)</label>
                                <div className="flex bg-slate-50 p-1 rounded-2xl border border-slate-100 shadow-inner">
                                    {[5, 10, 15, 20].map(c => (
                                        <button key={c} onClick={() => setBuilderCount(c)} className={`flex-1 py-3 rounded-xl text-xs font-black transition-all ${builderCount === c ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-400 hover:text-slate-600'}`}>{c}</button>
                                    ))}
                                </div>
                            </div>
                            <button 
                                onClick={handleBuildWithAI} 
                                disabled={isGenerating || !builderTopic.trim()} 
                                className="w-full py-6 bg-slate-900 text-white rounded-[1.5rem] font-black shadow-2xl flex items-center justify-center gap-4 border-b-4 border-black active:scale-95 disabled:opacity-50"
                            >
                                {isGenerating ? <Loader2 className="animate-spin" size={20}/> : <Wand2 size={20} />}
                                {isGenerating ? 'Synthesizing sequence...' : 'Build Logic Sequence'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="flex flex-col lg:flex-row justify-between items-stretch gap-6 bg-slate-900 p-8 rounded-[2.5rem] shadow-2xl border border-white/5 relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:rotate-12 transition-transform duration-1000 pointer-events-none"><Stethoscope size={160}/></div>
                <div className="relative z-10 flex-1">
                    <div className="flex items-center gap-3 mb-2">
                        <h2 className="text-3xl font-black text-white tracking-tight flex items-center gap-3"><Brain className="text-indigo-400" /> Scenario Registry</h2>
                        <div className="px-3 py-1 bg-indigo-500/20 text-indigo-400 text-[8px] font-black rounded-lg border border-indigo-500/30 flex items-center gap-2">
                            <Server size={10} /> {activeCollection}
                        </div>
                    </div>
                    <p className="text-slate-400 font-medium text-sm mt-1">Design narrative-driven case studies for certification preparation.</p>
                    
                    <div className="mt-8 flex flex-wrap gap-3">
                        <div className="bg-white/5 p-1 rounded-2xl border border-white/10 flex items-center">
                            <select value={certId} onChange={e => setCertId(e.target.value.toLowerCase())} className="bg-transparent text-white px-4 py-2 rounded-xl outline-none font-black text-[10px]">
                                {certs.map(c => <option key={c.id} value={c.id.toLowerCase()} className="text-black">{c.shortName}</option>)}
                            </select>
                        </div>
                        <button 
                            onClick={handleSuggestStrategy} 
                            disabled={isBlueprinting}
                            className="px-6 py-4 bg-indigo-600 text-white rounded-2xl font-black text-[10px] tracking-widest hover:bg-indigo-50 transition-all flex items-center gap-2 shadow-xl border-b-4 border-indigo-900 active:scale-95 disabled:opacity-50"
                        >
                            {isBlueprinting ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} 
                            Suggest Scenarios
                        </button>
                        <button onClick={() => { setBuilderTopic(''); setShowBuilder(true); }} className="px-8 py-4 bg-medical-500 text-white rounded-2xl font-black text-xs tracking-widest shadow-xl flex items-center gap-2 hover:bg-medical-400 transition-all border-b-4 border-medical-700">
                            <Wand2 size={18} /> AI Build Sequence
                        </button>
                        <button 
                            onClick={() => setSelectedScenario({ certId: certId.toLowerCase(), title: 'New Challenge', questionCount: 10, difficulty: 'Medium', isActive: true, questions: [], id: `manual-${Date.now()}` })} 
                            className="px-8 py-4 bg-white/10 text-white rounded-2xl font-black text-xs tracking-widest flex items-center gap-2 hover:bg-white/20 transition-all shadow-lg"
                        >
                            <Plus size={18}/> Manual Provision
                        </button>
                        <button 
                            onClick={handleExport}
                            disabled={isExporting || scenarios.length === 0}
                            className="px-8 py-4 bg-white/5 text-white rounded-2xl border border-white/10 font-black text-xs tracking-widest flex items-center gap-2 hover:bg-white/10 transition-all shadow-lg"
                        >
                            {isExporting ? <Loader2 size={18} className="animate-spin" /> : <Download size={18}/>} Export Backup
                        </button>
                        <button 
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isImporting}
                            className="px-8 py-4 bg-white/5 text-white rounded-2xl border border-white/10 font-black text-xs tracking-widest flex items-center gap-2 hover:bg-white/10 transition-all shadow-lg"
                        >
                            {isImporting ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18}/>} Ingest JSON
                        </button>
                        <input type="file" ref={fileInputRef} onChange={handleImportFile} accept=".json" className="hidden" />
                    </div>
                </div>
            </div>

            {status && (
                <div className={`p-4 rounded-3xl flex items-center justify-between animate-pop border-2 shadow-xl ${status.type === 'error' ? 'bg-rose-50 border-rose-100 text-rose-700' : 'bg-emerald-50 border-emerald-100 text-emerald-700'}`}>
                    <div className="flex items-center gap-3 ml-2">
                        {status.type === 'error' ? <AlertCircle size={18}/> : <CheckCircle size={18}/>}
                        <span className="font-black text-xs tracking-widest">{status.text}</span>
                    </div>
                    <button onClick={() => setStatus(null)} className="p-2 hover:bg-black/5 rounded-xl"><X size={16}/></button>
                </div>
            )}

            <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 flex-1 min-h-0">
                <div className="xl:col-span-4 bg-white rounded-[2.5rem] border border-slate-200 overflow-hidden flex flex-col shadow-sm">
                    <div className="p-6 bg-slate-50 border-b border-slate-100 font-black text-[10px] tracking-widest text-slate-400">Published Challenges</div>
                    <div className="flex-1 overflow-y-auto p-4 custom-scrollbar space-y-2">
                        {isLoading ? <PageLoader compact message="Loading Challenges..." /> : scenarios.length === 0 && suggestedTopics.length === 0 ? (
                            <div className="p-12 text-center opacity-20">
                                <Database size={48} className="mx-auto mb-4" />
                                <p className="font-black text-[10px]">Registry Empty</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {suggestedTopics.length > 0 && (
                                    <div className="mb-6 space-y-2 animate-slide-up">
                                        <p className="px-2 text-[8px] font-black text-indigo-400 tracking-widest">AI Strategy Suggestions</p>
                                        {suggestedTopics.map(topic => (
                                            <div key={topic} className="w-full p-2 bg-indigo-50 border-2 border-dashed border-indigo-200 rounded-xl flex items-center justify-between group hover:bg-white hover:border-indigo-500 transition-all">
                                                <span className="truncate text-indigo-700 text-[10px] font-bold px-1">{topic}</span>
                                                <div className="flex gap-1 shrink-0">
                                                    <button 
                                                        onClick={() => handleProvisionFromTopic(topic, true)}
                                                        className="p-1.5 bg-indigo-600 text-white rounded-lg shadow-md hover:bg-indigo-700 transition-all"
                                                        title="Provision + Build with AI"
                                                    >
                                                        <Wand2 size={12} />
                                                    </button>
                                                    <button 
                                                        onClick={() => handleProvisionFromTopic(topic, false)}
                                                        className="p-1.5 bg-white text-emerald-600 rounded-lg border border-emerald-100 shadow-sm hover:bg-emerald-50 transition-all"
                                                        title="Provision Empty"
                                                    >
                                                        <Plus size={12} />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {scenarios.map(s => (
                                    <div 
                                        key={s.id} 
                                        onClick={() => { setSelectedScenario(s); setStatus(null); }}
                                        className={`p-4 rounded-2xl border-2 transition-all cursor-pointer group flex items-center justify-between ${selectedScenario?.id === s.id ? 'bg-indigo-50 border-indigo-500 shadow-md' : 'bg-white border-transparent hover:bg-slate-50 shadow-sm'}`}
                                    >
                                        <div className="flex items-center gap-3 overflow-hidden">
                                            <div className={`p-2 rounded-lg ${selectedScenario?.id === s.id ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-400'}`}><Zap size={14} /></div>
                                            <div className="min-w-0">
                                                <p className="font-black text-[10px] text-slate-800 truncate">{s.title}</p>
                                                <p className="text-[7px] font-black text-slate-400 tracking-widest">{s.questions?.length || 0} Logic Items</p>
                                            </div>
                                        </div>
                                        <button onClick={(e) => { e.stopPropagation(); deleteScenario(s.id); }} className="p-1.5 text-slate-200 hover:text-rose-500 transition-all opacity-0 group-hover:opacity-100"><Trash2 size={14}/></button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {selectedScenario ? (
                    <div className="xl:col-span-8 bg-white rounded-[2.5rem] border border-slate-200 shadow-sm flex flex-col min-h-0 overflow-hidden relative">
                        <div className="flex-1 overflow-y-auto p-8 md:p-10 custom-scrollbar space-y-12 pb-32">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-6">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Challenge Title</label>
                                        <input type="text" value={selectedScenario.title} onChange={e => setSelectedScenario({...selectedScenario, title: e.target.value})} className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl font-black text-xl outline-none focus:bg-white transition-all shadow-inner" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Description Narrative</label>
                                        <textarea value={selectedScenario.description} onChange={e => setSelectedScenario({...selectedScenario, description: e.target.value})} className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl font-medium text-sm outline-none focus:bg-white h-24 resize-none" />
                                    </div>
                                </div>
                                <div className="space-y-8 bg-slate-50/50 p-6 rounded-[2rem] border border-slate-100">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <label className="text-[9px] font-black text-slate-400 tracking-widest">Question Count</label>
                                            <input type="number" value={selectedScenario.questions?.length || 0} disabled className="w-full p-3 bg-slate-100 border border-slate-200 rounded-xl font-black text-center opacity-60" />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[9px] font-black text-slate-400 tracking-widest">Difficulty</label>
                                            <select value={selectedScenario.difficulty} onChange={e => setSelectedScenario({...selectedScenario, difficulty: e.target.value as any})} className="w-full p-3 bg-white border border-slate-200 rounded-xl font-black text-[10px]">
                                                <option value="Easy">Easy</option><option value="Medium">Medium</option><option value="Hard">Hard</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between p-4 bg-white rounded-2xl shadow-sm border border-slate-100">
                                        <div>
                                            <p className="text-[10px] font-black text-slate-800">Operational Status</p>
                                            <p className={`text-[8px] font-bold ${selectedScenario.isActive ? 'text-emerald-500' : 'text-slate-400'}`}>{selectedScenario.isActive ? 'VISIBLE TO STUDENTS' : 'DRAFT/HIDDEN'}</p>
                                        </div>
                                        <button onClick={() => setSelectedScenario({...selectedScenario, isActive: !selectedScenario.isActive})} className={`relative w-12 h-6 rounded-full transition-all ${selectedScenario.isActive ? 'bg-indigo-600' : 'bg-slate-200'}`}>
                                            <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${selectedScenario.isActive ? 'translate-x-6' : 'translate-x-0'}`} />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-6">
                                <div className="flex items-center justify-between px-2">
                                    <h4 className="text-xl font-black text-slate-900 tracking-tight flex items-center gap-3"><ListChecks className="text-indigo-500" /> Sequence Logic</h4>
                                    <button onClick={() => setSelectedScenario({...selectedScenario, questions: [...(selectedScenario.questions || []), { _id: `q_${Date.now()}`, text: 'New Case Step', options: [{ _id: `o_${Date.now()}_1`, text: 'Choice A', isCorrect: true }], explanation: '' } as BackendQuestion]}) } className="px-6 py-2 bg-slate-900 text-white rounded-xl font-black text-[9px] tracking-widest">+ Add Step</button>
                                </div>

                                <div className="space-y-4">
                                    {(selectedScenario.questions || []).map((q, qIdx) => (
                                        <div key={q._id} className="p-8 bg-slate-50 border border-slate-100 rounded-[2.5rem] space-y-6 group/item shadow-inner relative">
                                            <button onClick={() => setSelectedScenario({...selectedScenario, questions: selectedScenario.questions?.filter((_, i) => i !== qIdx) })} className="absolute top-6 right-6 p-2 text-slate-300 hover:text-rose-500 opacity-0 group-hover/item:opacity-100 transition-all"><Trash2 size={16}/></button>
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center font-black text-sm text-indigo-600 shadow-sm">{qIdx + 1}</div>
                                                <input type="text" value={q.text} onChange={e => {
                                                    const next = [...(selectedScenario.questions || [])];
                                                    next[qIdx].text = e.target.value;
                                                    setSelectedScenario({...selectedScenario, questions: next});
                                                }} className="flex-1 bg-transparent border-none outline-none font-bold text-lg text-slate-900" placeholder="Question Step..." />
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                {(q.options || []).map((opt, oIdx) => (
                                                    <div key={opt._id} className={`p-4 rounded-2xl border-2 items-center gap-4 transition-all flex ${opt.isCorrect ? 'bg-emerald-50 border-emerald-500' : 'bg-white border-slate-100'}`}>
                                                        <button 
                                                            onClick={() => {
                                                                const next = [...(selectedScenario.questions || [])];
                                                                next[qIdx].options = next[qIdx].options.map((o, i) => ({ ...o, isCorrect: i === oIdx }));
                                                                setSelectedScenario({...selectedScenario, questions: next});
                                                            }}
                                                            className={`w-8 h-8 rounded-xl flex items-center justify-center font-black text-xs ${opt.isCorrect ? 'bg-emerald-500 text-white' : 'bg-white text-slate-300 border border-slate-200'}`}
                                                        >{opt.isCorrect ? <Check size={14}/> : String.fromCharCode(65+oIdx)}</button>
                                                        <input type="text" value={opt.text} onChange={e => {
                                                            const next = [...(selectedScenario.questions || [])];
                                                            next[qIdx].options[oIdx].text = e.target.value;
                                                            setSelectedScenario({...selectedScenario, questions: next});
                                                        }} className="flex-1 bg-transparent border-none outline-none font-bold text-xs" />
                                                    </div>
                                                ))}
                                            </div>

                                            <div className="space-y-2">
                                                <label className="text-[9px] font-black text-slate-400 tracking-widest ml-1">Rationale</label>
                                                <textarea value={q.explanation} onChange={e => {
                                                    const next = [...(selectedScenario.questions || [])];
                                                    next[qIdx].explanation = e.target.value;
                                                    setSelectedScenario({...selectedScenario, questions: next});
                                                }} className="w-full p-4 bg-white border border-slate-200 rounded-2xl font-medium text-xs resize-none h-20 outline-none" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="absolute bottom-0 left-0 right-0 p-8 border-t border-slate-100 bg-white flex justify-end gap-4 z-20">
                            <button onClick={() => setSelectedScenario(null)} className="px-8 py-4 font-black text-[10px] text-slate-400">Discard</button>
                            <button 
                                onClick={handleSave} disabled={isSaving}
                                className="px-12 py-4 bg-slate-900 text-white rounded-2xl font-black text-[10px] tracking-widest shadow-2xl flex items-center gap-3 border-b-4 border-black"
                            >
                                {isSaving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18}/>} Commit Logic
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="xl:col-span-8 flex flex-col items-center justify-center bg-slate-50 rounded-[3rem] border-4 border-dashed border-slate-200 opacity-60">
                        <div className="w-24 h-24 bg-white rounded-[2.5rem] flex items-center justify-center text-slate-200 shadow-xl mb-8"><Stethoscope size={48}/></div>
                        <h4 className="text-xl font-black text-slate-900 tracking-tighter">Scenario Workspace Ready</h4>
                        <p className="font-black text-slate-400 tracking-[0.3em] text-[10px] mt-2">Provision a challenge sequence to begin orchestration</p>
                    </div>
                )}
            </div>
        </div>
    );
};
